<template>
    <b-modal
        ref="languageModal"
        centered
        size="sm"
        :ok-title="$t('buttons.confirm')"
        :cancel-title="$t('buttons.cancel')"
        :ok-disabled="saving || loadingChoices || !selectedLanguage"
        :cancel-disabled="saving || loadingChoices"
        footer-class="d-flex align-items-center justify-content-center"
        @show="onShow"
        @ok="handleOk"
    >
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-center font-size-130 font-weight-bold text-center w-100">
                <b-icon icon="translate" variant="primary" class="mr-2"></b-icon>
                <span>{{ $t('langSwitcher.changeLanguage') }}</span>
            </div>
        </template>
        <b-alert v-if="error" show variant="danger" class="mb-3">
            {{ errorMsg }}
        </b-alert>

        <div v-if="loadingChoices" key="loadingChoices" class="d-flex align-items-center justify-content-center min-height-100">
            <b-spinner small class="mr-2"></b-spinner>
        </div>

        <div v-else key="choicesLoaded" class="">
            <LoadingContext :loading="loadingChoices || saving">
                <div class="font-size-90 text-center alert alert-warning mb-2">
                    {{ $t('langSwitcher.incompleteNote') }}
                </div>
                <b-form-group :label="finalLabel" class="mb-0" label-class="font-weight-bold">
                    <b-form-radio-group
                        v-model="selectedLanguage"
                        :options="languageOptions"
                        stacked
                        buttons
                        button-variant="outline-dark"
                        class="w-100 text-left"
                    ></b-form-radio-group>
                </b-form-group>
                <div class="font-size-90 mt-3 text-center alert alert-warning mb-0">
                    {{ $t('langSwitcher.switchNote') }}
                </div>
            </LoadingContext>
        </div>

        <template #modal-footer="{ ok, cancel }">
            <div class="d-flex justify-content-around gap-4 w-100">
                <b-button
                    variant="secondary"
                    :disabled="saving || loadingChoices"
                    class="w-50"
                    @click="cancel()"
                >
                    {{ finalCancelText }}
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="saving || loadingChoices || !selectedLanguage"
                    class="w-50"
                    @click="ok()"
                >
                    {{ finalOkText }}
                </b-button>
            </div>
        </template>

    </b-modal>
</template>

<script>
import { splUrlMapMixin } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import LoadingContext from './LoadingContext.vue';

export default {
    name: 'LanguageSwitcherModal',
    mixins: [splUrlMapMixin],
    components: {
        LoadingContext,
    },
    props: {},
    i18n: {
        messages: {
            en: {
                langSwitcher: {
                    changeLanguage: 'Change Language',
                    selectLanguage: 'Select a language',
                    save: 'Save',
                    cancel: 'Cancel',
                    switchNote: 'Note: This will reload the page to apply the new language.',
                    incompleteNote: 'Note: Some translations may be incomplete. Additional translations will be added in the coming weeks.',
                },
            },
            es: {
                langSwitcher: {
                    changeLanguage: 'Cambiar idioma',
                    selectLanguage: 'Seleccione un idioma',
                    save: 'Guardar',
                    cancel: 'Cancelar',
                    switchNote: 'Nota: Esto recargará la página para aplicar el nuevo idioma.',
                    incompleteNote: 'Nota: Algunas traducciones pueden estar incompletas. Se agregarán traducciones adicionales en las próximas semanas.',
                },
            },
            pt: {
                langSwitcher: {
                    changeLanguage: 'Alterar idioma',
                    selectLanguage: 'Selecione um idioma',
                    save: 'Salvar',
                    cancel: 'Cancelar',
                    switchNote: 'Nota: Isso recarregará a página para aplicar o novo idioma.',
                    incompleteNote: 'Nota: Algumas traduções podem estar incompletas. Traduções adicionais serão adicionadas nas próximas semanas.',
                },
            },
            'zh-Hans': {
                langSwitcher: {
                    changeLanguage: '更改语言',
                    selectLanguage: '选择语言',
                    save: '保存',
                    cancel: '取消',
                    switchNote: '注意：这将重新加载页面以应用新语言。',
                    incompleteNote: '注意：某些翻译可能不完整。未来几周将添加更多翻译。',
                },
            },
            'zh-Hant': {
                langSwitcher: {
                    changeLanguage: '更改語言',
                    selectLanguage: '選擇語言',
                    save: '保存',
                    cancel: '取消',
                    switchNote: '注意：這將重新加載頁面以應用新語言。',
                    incompleteNote: '注意：某些翻譯可能不完整。未來幾週將添加更多翻譯。',
                },
            },
        },
    },
    data: function() {
        return {
            languageChoices: [],  // from API
            loadingChoices: false,
            selectedLanguage: '',
            saving: false,
            error: false,
            errorMsg: '',
        }
    },
    computed: {
        finalLabel: function() {
            const transTitle = this.$t('langSwitcher.selectLanguage');
            if (this.currentLanguageCode.startsWith('en')) {
                return `${transTitle}:`;
            } else {
                return `${transTitle} / Select a language:`;  // show it in English too, so user can understand what it is
            }
        },
        finalOkText() {
            const transOk = this.$t('buttons.confirm');
            if (this.currentLanguageCode.startsWith('en')) {
                return transOk;
            } else {
                return `${transOk} / Confirm`;  // show it in English too, so user can understand what it is
            }
        },
        finalCancelText() {
            const transCancel = this.$t('buttons.cancel');
            if (this.currentLanguageCode.startsWith('en')) {
                return transCancel;
            } else {
                return `${transCancel} / Cancel`;  // show it in English too, so user can understand what it is
            }
        },
        currentLanguageCode: function() {
            return this.$i18nData.current_language || '';
        },
        languageOptions: function() {
            return this.languageChoices.map(choice => {
                // choice has name, name_local, name_translated, bidi, code
                const displayName = choice.name_local === choice.name_translated ? choice.name_local : `${choice.name_translated} (${choice.name_local})`;
                return {
                    value: choice.code,
                    text: displayName || choice.code,  
                };
            });
        },
    },
    methods: {
        show: function() {
            this.$refs.languageModal.show();
        },
        onShow: function() {
            this.selectedLanguage = this.currentLanguageCode || 'en';
            this.error = false;
            this.errorMsg = '';
            this.fetchChoices();  // fire and forget
        },
        handleOk: function(bvModalEvent) {
            // don't make this async or else we have problems with preventDefault() not working
            bvModalEvent.preventDefault();
            this.saveLanguage();
        },
        async fetchChoices() {
            this.languageChoices = [];
            this.loadingChoices = true;
            this.error = false;
            this.errorMsg = '';

            try {
                const response = await suppliosApi.i18n.listLanguages();
                this.languageChoices = response.data.choices || [];
                this.loadingChoices = false;
            } catch (error) {
                // TODO - use error handler...
                console.log('Error while fetching language choices');
                console.log(error);
                this.errorMsg = 'Error while fetching language choices';
                this.error = true;
                this.loadingChoices = false;
                this.$toast.error(this.errorMsg, {timeout: false});
            }
        },
        saveLanguage() {
            if (!this.selectedLanguage) {
                return;
            }
            if (this.selectedLanguage === this.currentLanguageCode) {
                this.$refs.languageModal.hide();
                return;
            }

            this.saving = true;
            this.error = false;
            this.errorMsg = '';

            // suppliosApi.i18n.setLanguage(this.selectedLanguage, this.nextUrl)
            suppliosApi.i18n.changeLanguage(this.selectedLanguage)
            .then(() => {
                // window.location.assign(this.nextUrl);
                window.location.reload();  // TODO - put up a warning that the page will reload
            })
            .catch(error => {
                console.log('Error while saving language');
                console.log(error);
                this.errorMsg = 'Error while saving language';
                this.error = true;
                this.saving = false;
                this.$toast.error(this.errorMsg, {timeout: false});
            });
        },
    },
}
</script>
